/* ========== EXCEL TABLE (ListObject) STYLES ==========
 * Excel "Format as Table" ranges — used by most Excel templates — carry
 * their colours in a named TABLE STYLE (TableStyleMedium2, TableStyleLight9,
 * a custom style in styles.xml, ...) instead of per-cell fills. ExcelJS does
 * not expose those colours, so without this module template tables render as
 * plain black-on-white grids (QA: "Excel template colours are not displayed").
 *
 * This module:
 *   1. finds every table part (xl/tables/tableN.xml) and the sheet it lives on,
 *   2. resolves its style to concrete CSS colours using the workbook THEME
 *      (built-in Light/Medium/Dark 1-28 families, or a custom <tableStyle>
 *      defined in styles.xml via its dxf records),
 *   3. applies those colours to the sheet's cell style map. Direct cell
 *      formatting always wins, exactly like Excel.
 * ===================================================== */

import { qLocal, qLocalAll } from "./xmlUtils";
import { applyTintToHex } from "../utils/colorUtils";

const REL_NS = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";
const OFFICE_THEME = [
  "FFFFFF", "000000", "E7E6E6", "44546A",
  "4472C4", "ED7D31", "A5A5A5", "FFC000", "5B9BD5", "70AD47",
];

function colIdx(label) {
  let n = 0;
  for (let i = 0; i < label.length; i += 1) n = n * 26 + (label.toUpperCase().charCodeAt(i) - 64);
  return n - 1;
}

// "A1:D20" -> { r1, c1, r2, c2 } (0-based) or null
function parseRangeRef(ref) {
  const m = /^\$?([A-Za-z]+)\$?(\d+)(?::\$?([A-Za-z]+)\$?(\d+))?$/.exec(String(ref || "").trim());
  if (!m) return null;
  const r1 = parseInt(m[2], 10) - 1;
  const c1 = colIdx(m[1]);
  const r2 = m[4] ? parseInt(m[4], 10) - 1 : r1;
  const c2 = m[3] ? colIdx(m[3]) : c1;
  return { r1: Math.min(r1, r2), r2: Math.max(r1, r2), c1: Math.min(c1, c2), c2: Math.max(c1, c2) };
}

// Resolve a relationship Target relative to the part that owns the rels.
function resolveTarget(basePath, target) {
  if (!target) return null;
  if (target.charAt(0) === "/") return target.slice(1);
  const parts = basePath.split("/");
  parts.pop();
  target.split("/").forEach((seg) => {
    if (seg === "..") parts.pop();
    else if (seg !== ".") parts.push(seg);
  });
  return parts.join("/");
}

/**
 * Find every table in the workbook.
 * @returns {Promise<{[sheetName]: Array<object>}>}
 */
export async function extractTablesFromZip(zip) {
  const out = {};
  if (!zip) return out;
  try {
    const wbFile = zip.file("xl/workbook.xml");
    const relsFile = zip.file("xl/_rels/workbook.xml.rels");
    if (!wbFile || !relsFile) return out;
    const parser = new DOMParser();
    const wbDoc = parser.parseFromString(await wbFile.async("string"), "application/xml");
    const relsDoc = parser.parseFromString(await relsFile.async("string"), "application/xml");

    const relTargets = {};
    qLocalAll(relsDoc, "Relationship").forEach((rel) => {
      relTargets[rel.getAttribute("Id")] = resolveTarget("xl/workbook.xml", rel.getAttribute("Target"));
    });

    const sheets = qLocalAll(wbDoc, "sheet").map((node) => ({
      name: node.getAttribute("name"),
      path: relTargets[node.getAttributeNS(REL_NS, "id") || node.getAttribute("r:id")],
    }));

    for (const { name, path } of sheets) {
      if (!name || !path) continue;
      const parts = path.split("/");
      const file = parts.pop();
      const sheetRelsPath = `${parts.join("/")}/_rels/${file}.rels`;
      const sheetRels = zip.file(sheetRelsPath);
      if (!sheetRels) continue;
      // eslint-disable-next-line no-await-in-loop
      const srDoc = parser.parseFromString(await sheetRels.async("string"), "application/xml");
      const tablePaths = qLocalAll(srDoc, "Relationship")
        .filter((rel) => /\/table$/.test(rel.getAttribute("Type") || ""))
        .map((rel) => resolveTarget(path, rel.getAttribute("Target")));

      for (const tp of tablePaths) {
        const tFile = tp && zip.file(tp);
        if (!tFile) continue;
        // eslint-disable-next-line no-await-in-loop
        const tDoc = parser.parseFromString(await tFile.async("string"), "application/xml");
        const tEl = tDoc.documentElement;
        const range = parseRangeRef(tEl.getAttribute("ref"));
        if (!range) continue;
        const info = qLocal(tDoc, "tableStyleInfo");
        const styleName = info ? info.getAttribute("name") : null;
        if (!styleName) continue; // "None" style — nothing to paint
        const flag = (attr, dflt) => {
          const v = info.getAttribute(attr);
          if (v == null) return dflt;
          return v === "1" || v === "true";
        };
        const hdr = tEl.getAttribute("headerRowCount");
        const tot = tEl.getAttribute("totalsRowCount");
        (out[name] || (out[name] = [])).push({
          range,
          styleName,
          headerRowCount: hdr == null ? 1 : parseInt(hdr, 10) || 0,
          totalsRowCount: tot != null ? parseInt(tot, 10) || 0 : 0,
          showRowStripes: flag("showRowStripes", true),
          showColumnStripes: flag("showColumnStripes", false),
          showFirstColumn: flag("showFirstColumn", false),
          showLastColumn: flag("showLastColumn", false),
        });
      }
    }
  } catch (err) {
    // Never let table styling break the viewer.
    console.warn("[tableStyleParser] table extraction failed", err);
  }
  return out;
}

/**
 * Custom table styles from styles.xml:
 *   <tableStyles><tableStyle name="X"><tableStyleElement type="headerRow" dxfId="3"/>…
 * @returns {{[name]: {[elementType]: dxfId}}}
 */
export function extractCustomTableStyles(stylesDoc) {
  const out = {};
  if (!stylesDoc) return out;
  qLocalAll(stylesDoc, "tableStyle").forEach((ts) => {
    const name = ts.getAttribute("name");
    if (!name) return;
    const elements = {};
    qLocalAll(ts, "tableStyleElement").forEach((el) => {
      const type = el.getAttribute("type");
      const dxfId = el.getAttribute("dxfId");
      if (type && dxfId != null) elements[type] = parseInt(dxfId, 10);
    });
    out[name] = elements;
  });
  return out;
}

/* ---------------- built-in style resolution ---------------- */

const tint = (hex, t) => `#${applyTintToHex(hex, t)}`;
const line = (w, color, kind = "solid") => `${w}px ${kind} ${color}`;

/**
 * Resolve a built-in table style name to a spec of CSS parts:
 *   { wholeTable, header, total, band1Row, band1Col, firstCol, lastCol,
 *     innerH, innerV, outline }
 * Each part: { bg, color, bold } ; borders are CSS strings.
 * Returns null for unknown names.
 */
export function resolveBuiltInTableStyle(styleName, themeColors) {
  const m = /^TableStyle(Light|Medium|Dark)(\d+)$/.exec(String(styleName || ""));
  if (!m) return null;
  const family = m[1];
  const num = parseInt(m[2], 10);
  const theme = (i) => (Array.isArray(themeColors) && themeColors[i]) || OFFICE_THEME[i];
  const dk1 = theme(1);
  const accentFor = (n) => {
    const idx = (n - 1) % 7; // 0 = dark1 (neutral), 1..6 = accent1..6
    return idx === 0 ? dk1 : theme(3 + idx);
  };
  const isNeutral = (n) => (n - 1) % 7 === 0;
  const C = accentFor(num);
  const white = "#FFFFFF";

  if (family === "Light") {
    if (num <= 7) {
      return {
        wholeTable: { color: isNeutral(num) ? null : tint(C, -0.25) },
        header: { bold: true, borderBottom: line(1, `#${C}`) },
        band1Row: { bg: tint(C, isNeutral(num) ? 0.85 : 0.8) },
        band1Col: { bg: tint(C, isNeutral(num) ? 0.85 : 0.8) },
        total: { bold: true, borderTop: line(3, `#${C}`, "double") },
        firstCol: { bold: true }, lastCol: { bold: true },
        outline: { top: line(1, `#${C}`), bottom: line(1, `#${C}`) },
      };
    }
    if (num <= 14) {
      return {
        header: { bg: `#${C}`, color: white, bold: true },
        band1Row: { borderTop: line(1, `#${C}`), borderBottom: line(1, `#${C}`) },
        band1Col: { borderLeft: line(1, `#${C}`), borderRight: line(1, `#${C}`) },
        total: { bold: true, borderTop: line(3, `#${C}`, "double") },
        firstCol: { bold: true }, lastCol: { bold: true },
        outline: { top: line(1, `#${C}`), bottom: line(1, `#${C}`), left: line(1, `#${C}`), right: line(1, `#${C}`) },
      };
    }
    return { // 15-21
      header: { bold: true, borderBottom: line(2, `#${C}`) },
      band1Row: { bg: tint(C, isNeutral(num) ? 0.85 : 0.8) },
      band1Col: { bg: tint(C, isNeutral(num) ? 0.85 : 0.8) },
      total: { bold: true, borderTop: line(3, `#${C}`, "double") },
      firstCol: { bold: true }, lastCol: { bold: true },
      innerH: line(1, `#${C}`), innerV: line(1, `#${C}`),
      outline: { top: line(1, `#${C}`), bottom: line(1, `#${C}`), left: line(1, `#${C}`), right: line(1, `#${C}`) },
    };
  }

  if (family === "Medium") {
    if (num <= 7) {
      const b = tint(C, isNeutral(num) ? 0.5 : 0.4);
      return {
        header: { bg: `#${C}`, color: white, bold: true },
        band1Row: { bg: tint(C, isNeutral(num) ? 0.85 : 0.8) },
        band1Col: { bg: tint(C, isNeutral(num) ? 0.85 : 0.8) },
        total: { bold: true, borderTop: line(3, `#${C}`, "double") },
        firstCol: { bold: true }, lastCol: { bold: true },
        innerH: line(1, b),
        outline: { top: line(1, b), bottom: line(1, b), left: line(1, b), right: line(1, b) },
      };
    }
    if (num <= 14) {
      return {
        wholeTable: { bg: tint(C, isNeutral(num) ? 0.65 : 0.8) },
        header: { bg: `#${C}`, color: white, bold: true, borderBottom: line(3, white) },
        band1Row: { bg: tint(C, isNeutral(num) ? 0.85 : 0.6) },
        band1Col: { bg: tint(C, isNeutral(num) ? 0.85 : 0.6) },
        total: { bg: `#${C}`, color: white, bold: true, borderTop: line(3, white) },
        firstCol: { bg: `#${C}`, color: white, bold: true },
        lastCol: { bg: `#${C}`, color: white, bold: true },
        innerH: line(1, white), innerV: line(1, white),
      };
    }
    if (num <= 21) {
      const b = `#${dk1}`;
      return {
        header: { bg: `#${C}`, color: white, bold: true, borderBottom: line(2, b) },
        band1Row: { bg: tint(dk1, 0.85) },
        band1Col: { bg: tint(dk1, 0.85) },
        total: { bold: true, borderTop: line(3, b, "double") },
        firstCol: { bold: true }, lastCol: { bold: true },
        innerH: line(1, b),
        outline: { top: line(1, b), bottom: line(1, b), left: line(1, b), right: line(1, b) },
      };
    }
    // 22-28
    const b = tint(C, isNeutral(num) ? 0.5 : 0.4);
    return {
      wholeTable: { bg: tint(C, isNeutral(num) ? 0.85 : 0.8) },
      header: { bold: true },
      band1Row: { bg: tint(C, isNeutral(num) ? 0.65 : 0.6) },
      band1Col: { bg: tint(C, isNeutral(num) ? 0.65 : 0.6) },
      total: { bold: true, borderTop: line(3, `#${C}`, "double") },
      firstCol: { bold: true }, lastCol: { bold: true },
      innerH: line(1, b), innerV: line(1, b),
      outline: { top: line(1, b), bottom: line(1, b), left: line(1, b), right: line(1, b) },
    };
  }

  // Dark
  if (num <= 7) {
    return {
      wholeTable: { bg: isNeutral(num) ? tint(dk1, 0.45) : tint(C, -0.25), color: white },
      header: { bg: `#${dk1}`, color: white, bold: true, borderBottom: line(2, white) },
      band1Row: { bg: isNeutral(num) ? tint(dk1, 0.25) : tint(C, -0.5) },
      band1Col: { bg: isNeutral(num) ? tint(dk1, 0.25) : tint(C, -0.5) },
      total: { bg: isNeutral(num) ? tint(dk1, 0.15) : tint(C, -0.5), color: white, bold: true, borderTop: line(2, white) },
      firstCol: { bg: isNeutral(num) ? tint(dk1, 0.25) : tint(C, -0.5), color: white, bold: true },
      lastCol: { bg: isNeutral(num) ? tint(dk1, 0.25) : tint(C, -0.5), color: white, bold: true },
    };
  }
  // Dark 8-11 — light body under a dark header (paired accents).
  const pair = { 8: [1, 1], 9: [4, 5], 10: [6, 7], 11: [8, 9] }[num] || [4, 5];
  const body = theme(pair[0]);
  const head = theme(pair[1]);
  return {
    wholeTable: { bg: tint(body, pair[0] === 1 ? 0.85 : 0.8) },
    header: { bg: `#${pair[1] === 1 ? dk1 : head}`, color: white, bold: true },
    band1Row: { bg: tint(body, pair[0] === 1 ? 0.65 : 0.6) },
    band1Col: { bg: tint(body, pair[0] === 1 ? 0.65 : 0.6) },
    total: { bold: true, borderTop: line(3, `#${dk1}`, "double") },
    firstCol: { bold: true }, lastCol: { bold: true },
  };
}

/** Build a spec from a custom <tableStyle> using the workbook's dxf records. */
function resolveCustomTableStyle(elements, dxfStyles) {
  if (!elements || !Array.isArray(dxfStyles)) return null;
  const part = (type) => {
    const d = elements[type] != null ? dxfStyles[elements[type]] : null;
    if (!d) return null;
    return { bg: d.backgroundColor || null, color: d.color || null, bold: d.fontWeight === 700 };
  };
  return {
    wholeTable: part("wholeTable"),
    header: part("headerRow"),
    total: part("totalRow"),
    band1Row: part("firstRowStripe"),
    band2Row: part("secondRowStripe"),
    band1Col: part("firstColumnStripe"),
    band2Col: part("secondColumnStripe"),
    firstCol: part("firstColumn"),
    lastCol: part("lastColumn"),
  };
}

// A background counts as "no fill" when missing / transparent / plain white.
const isEmptyBg = (bg) => !bg || /^(#fff(fff)?|white|transparent)$/i.test(String(bg));
// Default (automatic) text colour — a table style may recolour it.
const isDefaultColor = (c) => !c || /^#0{6}$/.test(String(c));

/**
 * Paint every table of one sheet into its `styles` map (mutates it).
 * A cell's own fill / font colour always wins over the table style.
 */
export function applyTableStyles(tables, styles, { themeColors, dxfStyles, customTableStyles }) {
  if (!Array.isArray(tables) || tables.length === 0) return;
  tables.forEach((t) => {
    const spec =
      (customTableStyles && customTableStyles[t.styleName]
        ? resolveCustomTableStyle(customTableStyles[t.styleName], dxfStyles)
        : null) || resolveBuiltInTableStyle(t.styleName, themeColors);
    if (!spec) return;

    const { r1, r2, c1, c2 } = t.range;
    const headerEnd = r1 + (t.headerRowCount || 0) - 1;          // last header row
    const totalStart = r2 - (t.totalsRowCount || 0) + 1;          // first totals row
    const dataStart = headerEnd + 1;

    for (let r = r1; r <= r2; r += 1) {
      const isHeader = r <= headerEnd;
      const isTotal = !isHeader && r >= totalStart && t.totalsRowCount > 0;
      const dataIdx = r - dataStart;
      for (let c = c1; c <= c2; c += 1) {
        const key = `${r}:${c}`;
        const s = styles[key] || {};
        const hadFill = !isEmptyBg(s.backgroundColor);
        const hadColor = !isDefaultColor(s.color);

        // Layer parts from lowest to highest precedence (Excel order).
        const layers = [spec.wholeTable];
        if (!isHeader && !isTotal) {
          if (t.showColumnStripes) layers.push((c - c1) % 2 === 0 ? spec.band1Col : spec.band2Col);
          if (t.showRowStripes) layers.push(dataIdx % 2 === 0 ? spec.band1Row : spec.band2Row);
        }
        if (t.showFirstColumn && c === c1) layers.push(spec.firstCol);
        if (t.showLastColumn && c === c2) layers.push(spec.lastCol);
        if (isHeader) layers.push(spec.header);
        if (isTotal) layers.push(spec.total);

        let bg = null; let color = null; let bold = false;
        const borders = {};
        layers.forEach((p) => {
          if (!p) return;
          if (p.bg) bg = p.bg;
          if (p.color) color = p.color;
          if (p.bold) bold = true;
          ["borderTop", "borderBottom", "borderLeft", "borderRight"].forEach((k) => {
            if (p[k]) borders[k] = p[k];
          });
        });
        // Inner grid lines + outline.
        if (spec.innerH) {
          if (r > r1 && !borders.borderTop) borders.borderTop = spec.innerH;
          if (r < r2 && !borders.borderBottom) borders.borderBottom = spec.innerH;
        }
        if (spec.innerV) {
          if (c > c1 && !borders.borderLeft) borders.borderLeft = spec.innerV;
          if (c < c2 && !borders.borderRight) borders.borderRight = spec.innerV;
        }
        if (spec.outline) {
          if (r === r1 && spec.outline.top) borders.borderTop = borders.borderTop || spec.outline.top;
          if (r === r2 && spec.outline.bottom) borders.borderBottom = borders.borderBottom || spec.outline.bottom;
          if (c === c1 && spec.outline.left) borders.borderLeft = borders.borderLeft || spec.outline.left;
          if (c === c2 && spec.outline.right) borders.borderRight = borders.borderRight || spec.outline.right;
        }

        let changed = false;
        if (bg && !hadFill) { s.backgroundColor = bg; changed = true; }
        if (color && !hadColor) { s.color = color; changed = true; }
        if (bold && !s.fontWeight) { s.fontWeight = 700; changed = true; }
        Object.keys(borders).forEach((k) => {
          if (!s[k]) { s[k] = borders[k]; changed = true; }
        });
        if (changed) styles[key] = s;
      }
    }
  });
}
